import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Minus, Plus, Package, Check } from "lucide-react";
import { API, PRODUCT_BANNER_IMAGE } from "../constants";
import { useCart } from "../context/CartContext";
import ProductReviews from "../components/ProductReviews";
import ProductImageCarousel from "../components/ProductImageCarousel";
import ProductQA from "../components/ProductQA";
import WishlistButton from "../components/WishlistButton";
import RelatedProducts from "../components/RelatedProducts";
import RecentlyViewed, { trackProductView } from "../components/RecentlyViewed";

const ProductDetailPage = () => {
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const location = useLocation();
  const productId = location.pathname.split('/').pop();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${productId}`);
        setProduct(response.data);
        trackProductView(response.data.product_id);
      } catch (error) {
        console.error('Failed to fetch product');
      }
      setLoading(false);
    };
    fetchProduct();
  }, [productId]);

  if (loading) return (
    <div className="min-h-screen bg-[#050505] pt-24 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D62828]"></div>
    </div>
  );

  if (!product) return (
    <div className="min-h-screen bg-[#050505] pt-24 flex items-center justify-center">
      <p className="text-gray-400 text-lg">Product not found</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
        <div className="mb-10">
          <div className="w-full h-40 sm:h-52 lg:h-60 rounded-sm overflow-hidden relative">
            <img src={PRODUCT_BANNER_IMAGE} alt="Cut Above Product Detail" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/40 to-black/75 flex items-center px-6 sm:px-10">
              <div>
                <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#D62828] mb-1">Cut Above Polish Shop</p>
                <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-wide">Professional Polishing & Detailing Gear</h2>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-testid="product-detail">
          <ProductImageCarousel
            images={[
              ...(product.images || []),
              ...(product.image_url && !(product.images || []).includes(product.image_url) ? [product.image_url] : []),
            ]}
            fallback={product.image_url}
            name={product.name}
          />
          <div>
            <p className="text-gray-500 uppercase tracking-wide text-sm mb-2">{product.category}</p>
            <div className="flex items-start justify-between gap-4 mb-4">
              <h1 className="font-heading text-4xl tracking-wide" data-testid="product-name">{product.name}</h1>
              <WishlistButton productId={product.product_id} className="flex-shrink-0 mt-1" />
            </div>
            <div className="flex items-baseline gap-3 mb-6">
              <p className="text-[#D62828] text-3xl font-bold" data-testid="product-price">${product.online_price.toFixed(2)}</p>
              {product.msrp && product.msrp > product.online_price && (
                <>
                  <p className="text-gray-500 text-xl line-through" data-testid="product-msrp">${product.msrp.toFixed(2)}</p>
                  <span className="text-green-500 text-sm font-semibold" data-testid="product-savings">Save {Math.round((1 - product.online_price / product.msrp) * 100)}%</span>
                </>
              )}
            </div>
            <p className="text-gray-300 mb-8 leading-relaxed">{product.description}</p>
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-400">Quantity:</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 bg-[#1A1A1A] border border-white/10 rounded-sm flex items-center justify-center hover:border-white/30 transition-colors">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-16 h-10 bg-[#1A1A1A] border border-white/10 rounded-sm flex items-center justify-center font-semibold">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="w-10 h-10 bg-[#1A1A1A] border border-white/10 rounded-sm flex items-center justify-center hover:border-white/30 transition-colors">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
            <button onClick={() => addToCart(product.product_id, quantity)} className="w-full bg-[#D62828] hover:bg-[#B91C1C] text-white py-4 rounded-sm font-bold uppercase tracking-wider transition-all btn-glow flex items-center justify-center gap-2" data-testid="add-to-cart-btn">
              <ShoppingCart className="w-5 h-5" />Add to Cart
            </button>
            <div className="mt-8 space-y-4 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-500" />
                {product.inventory_count > 0 ? `${product.inventory_count} in stock` : 'Out of stock'}
              </div>
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-[#D62828]" />
                {product.fulfillment === 'Stock' ? 'Ships from warehouse' : 'Ships direct from vendor'}
              </div>
            </div>
          </div>
        </div>
        <ProductReviews productId={product.product_id} />
        <ProductQA productId={product.product_id} />
        <RelatedProducts productId={product.product_id} />
        <RecentlyViewed excludeProductId={product.product_id} />
      </div>
    </div>
  );
};

export default ProductDetailPage;
